import { SoftwareSpecContext } from "@/crud/software/context";
import { getSoftwareSpecYaml } from "@/crud/software/fetch";
import { useWorkspaceContextSlug } from "@/utils/hooks";
import { LoadingButton } from "@mui/lab";
import { Box, Tooltip } from "@mui/material";
import {
  DataGridPro,
  GridCheckIcon,
  GridCloseIcon,
  GridColDef,
} from "@mui/x-data-grid-pro";
import React, { useContext } from "react";
import { useQuery } from "react-query";
import { unifyPackageSpec, SoftwareSpecRow } from "./utils";

const gridColumns: GridColDef<SoftwareSpecRow>[] = [
  {
    field: "name",
    headerName: "Package Name",
    flex: 1.5,
    valueGetter: (params) => {
      return params.row.condaName || params.row.name || "";
    },
  },
  { field: "specifier", headerName: "Specifier", flex: 1 },
  {
    field: "clientVersion",
    headerName: "Local Version",
    flex: 1,
    valueGetter: (params) => params.row.clientVersion || "",
  },
  {
    field: "source",
    headerName: "Source",
    flex: 0.75,
    valueGetter: (params) => (params.row.file ? "File" : params.row.source),
  },
  {
    field: "requested",
    headerName: "Requested",
    type: "boolean",
    flex: 0.5,
    valueGetter: (params) => params.row.requested ?? false,
  },
  {
    field: "channel",
    headerName: "Channel",
    flex: 2,
    valueGetter: (params) => params.row.channel || "",
  },
  {
    field: "filename",
    headerName: "File",
    flex: 1.5,
    valueGetter: (params) => params.row.file?.fileName,
  },
  {
    field: "include",
    headerName: "Include",
    type: "boolean",
    flex: 0.75,
    valueGetter: (params) => params.row.include ?? false,
    renderCell: (params) => {
      const error = params.row.error;
      const include = params.row.include ?? false;
      const IconComponent = include ? GridCheckIcon : GridCloseIcon;

      if (!include) {
        return (
          <Tooltip title={error || "Package ignored"} arrow>
            <IconComponent
              fontSize="small"
              sx={{
                fontWeight: 400,
                opacity: 0.5,
                strokeWidth: 1,
              }}
            />
          </Tooltip>
        );
      }

      return (
        <IconComponent
          fontSize="small"
          sx={{
            fontWeight: 400,
            opacity: 0.5,
            strokeWidth: 1,
          }}
        />
      );
    },
  },
];
const ExportYamlButton = () => {
  const account = useWorkspaceContextSlug();
  const spec = useContext(SoftwareSpecContext);
  const { data, isFetched } = useQuery(
    ["get-spec-yaml", spec.id],
    () => getSoftwareSpecYaml(account, spec.id),
    { enabled: !!spec.id },
  );
  const handleDownload = () => {
    if (data) {
      const link = document.createElement("a");
      link.href = "data:text/yaml," + encodeURIComponent(data);
      link.download = `env.yaml`;
      link.click();
    }
  };
  return (
    <Box sx={{ padding: 1 }}>
      <LoadingButton loading={!isFetched} onClick={handleDownload}>
        Export for Conda
      </LoadingButton>
    </Box>
  );
};

export const SoftwareSpecTable = (): React.ReactElement => {
  const spec = useContext(SoftwareSpecContext);
  const combinedRows = unifyPackageSpec(spec);
  return (
    <DataGridPro
      sx={{
        height: "100%",
        border: "unset",
        "& .excluded-row": {
          backgroundColor: "rgba(255, 73, 73, 0.08)",
          "&:hover": {
            backgroundColor: "rgba(255, 73, 73, 0.12)",
          },
        },
        "& .greyed-out-row": {
          opacity: 0.5,
        },
      }}
      pagination={false}
      getRowClassName={(params) => {
        const { include, error } = params.row;
        if (!include && error && error !== "Package ignored") {
          return "excluded-row";
        }
        if (!include) {
          return "greyed-out-row";
        }
        return "";
      }}
      initialState={{
        sorting: {
          sortModel: [{ field: "name", sort: "asc" }],
        },
      }}
      slots={{
        footer: ExportYamlButton,
      }}
      columns={gridColumns}
      rows={combinedRows}
    />
  );
};
